import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase, fromTable } from '@/integrations/supabase/client';
import { CollectiveFeedbackRequest } from '@/types/collective-feedback';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarDays, Mail, MapPin, Users, ClipboardList, Send, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface CollectiveFeedbacksListProps {
  canCreate?: boolean;
}

const CollectiveFeedbacksList: React.FC<CollectiveFeedbacksListProps> = ({ 
  canCreate = true 
}) => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [feedbacks, setFeedbacks] = useState<CollectiveFeedbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<string>('active');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<CollectiveFeedbackRequest | null>(null);
  const [recipientCounts, setRecipientCounts] = useState<Record<string, { total: number, completed: number, sent: number }>>({}); 
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFeedbacks();
    }
  }, [user, selectedTab]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      
      let query = fromTable('collective_feedback_requests')
        .select('*');
      
      // Filter by corporate group if user is in one
      // Check if corporate_group_id exists on the profile before using it
      if (profile && 'corporate_group_id' in profile && profile.corporate_group_id) {
        query = query.eq('corporate_group_id', profile.corporate_group_id);
      } else {
        query = query.eq('creator_id', user!.id);
      }
      
      // Filter by status
      if (selectedTab === 'active') {
        query = query.in('status', ['pending', 'active']);
      } else if (selectedTab === 'completed') {
        query = query.eq('status', 'completed');
      } else if (selectedTab === 'canceled') {
        query = query.eq('status', 'canceled');
      }
      
      // Order by creation date
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setFeedbacks(data as CollectiveFeedbackRequest[] || []);
      
      // Fetch recipient counts for each request
      if (data && data.length > 0) {
        const counts: Record<string, { total: number, completed: number, sent: number }> = {};
        
        await Promise.all(data.map(async (request) => {
          const { data: recipientsData, error: recipientsError } = await fromTable('collective_feedback_recipients')
            .select('status')
            .eq('request_id', request.id);
            
          if (!recipientsError && recipientsData) {
            const total = recipientsData.length;
            const completed = recipientsData.filter(r => r.status === 'completed').length;
            const sent = recipientsData.filter(r => r.status === 'sent' || r.status === 'completed').length;
            
            counts[request.id] = { total, completed, sent };
          }
        }));
        
        setRecipientCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('Erreur lors du chargement des demandes de feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessingAction(true);
      
      const { error } = await supabase.functions.invoke('send-collective-feedback', {
        body: { 
          requestId: selectedRequest.id
        }
      });
      
      if (error) throw error;
      
      // Update status
      await fromTable('collective_feedback_recipients')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString() 
        })
        .eq('request_id', selectedRequest.id)
        .eq('status', 'pending');
        
      await fromTable('collective_feedback_requests')
        .update({ status: 'active' })
        .eq('id', selectedRequest.id);
      
      toast.success('Emails envoyés avec succès');
      setDetailDialogOpen(false);
      fetchFeedbacks();
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error('Erreur lors de l\'envoi des emails');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessingAction(true);
      
      // Delete recipients first (cascade should handle this, but being explicit)
      await fromTable('collective_feedback_recipients')
        .delete()
        .eq('request_id', selectedRequest.id);
        
      // Delete the request
      const { error } = await fromTable('collective_feedback_requests')
        .delete()
        .eq('id', selectedRequest.id);
        
      if (error) throw error;
      
      toast.success('Demande de feedback supprimée');
      setConfirmDeleteDialogOpen(false);
      setDetailDialogOpen(false);
      fetchFeedbacks();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Erreur lors de la suppression de la demande');
    } finally {
      setProcessingAction(false);
    }
  };

  const showRequestDetails = (request: CollectiveFeedbackRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">En attente d'envoi</Badge>;
      case 'active':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">En cours</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Terminée</Badge>;
      case 'canceled':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Annulée</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getContextLabel = (context: string) => {
    switch (context) {
      case 'recruitment':
        return 'Recrutement';
      case 'freelance':
        return 'Mission / Projet';
      case '360':
        return 'Évaluation 360°';
      case 'all':
        return 'Autre';
      default:
        return context;
    }
  };

  const getProgressPercentage = (requestId: string) => {
    const counts = recipientCounts[requestId];
    if (!counts || counts.total === 0) return 0;
    return Math.round((counts.completed / counts.total) * 100);
  };

  const renderFeedbackCards = () => {
    if (loading) {
      return Array(3).fill(0).map((_, index) => (
        <Card key={index} className="mb-4">
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/4" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </CardContent>
        </Card>
      ));
    }

    if (feedbacks.length === 0) {
      return (
        <Alert className="mb-6">
          <AlertTitle>Aucune demande</AlertTitle>
          <AlertDescription>
            {selectedTab === 'active' 
              ? "Vous n'avez pas de demandes de feedback actives."
              : selectedTab === 'completed'
                ? "Vous n'avez pas de demandes de feedback complétées."
                : "Vous n'avez pas de demandes de feedback annulées."}
          </AlertDescription>
        </Alert>
      );
    }

    return feedbacks.map((feedback) => (
      <Card key={feedback.id} className="mb-4 hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg">{feedback.title}</CardTitle>
            {getStatusBadge(feedback.status)}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary">{getContextLabel(feedback.context_type)}</Badge>
            {feedback.event_type && (
              <Badge variant="outline">{feedback.event_type}</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {feedback.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{feedback.description}</p>
          )}
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            {feedback.event_date && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <CalendarDays className="h-4 w-4" />
                <span>{format(new Date(feedback.event_date), 'PPP', { locale: fr })}</span>
              </div>
            )}
            
            {feedback.event_location && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{feedback.event_location}</span>
              </div>
            )}
          </div>
          
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Progression</span>
              <span className="font-medium">
                {recipientCounts[feedback.id]?.completed || 0}/{recipientCounts[feedback.id]?.total || 0} ({getProgressPercentage(feedback.id)}%)
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div 
                className="bg-green-600 h-2.5 rounded-full" 
                style={{ width: `${getProgressPercentage(feedback.id)}%` }}
              ></div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="pt-0">
          <Button 
            variant="outline"
            className="w-full" 
            onClick={() => showRequestDetails(feedback)}
          >
            Voir détails
          </Button>
        </CardFooter>
      </Card>
    ));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Demandes de feedback collectives</h2>
        {canCreate && (
          <Button onClick={() => navigate('/request-collective-feedback')}>
            Nouvelle demande
          </Button>
        )}
      </div>
      
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="active">Actives</TabsTrigger>
          <TabsTrigger value="completed">Terminées</TabsTrigger>
          <TabsTrigger value="canceled">Annulées</TabsTrigger>
        </TabsList>
      </Tabs>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderFeedbackCards()}
      </div>
      
      {/* Request Details Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedRequest && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {selectedRequest.title}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-2">
                  {getStatusBadge(selectedRequest.status)}
                  <Badge variant="secondary">{getContextLabel(selectedRequest.context_type)}</Badge>
                  {selectedRequest.event_type && (
                    <Badge variant="outline">{selectedRequest.event_type}</Badge>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {selectedRequest.description && (
                  <div>
                    <h3 className="text-sm font-medium mb-1">Description</h3>
                    <p className="text-sm text-muted-foreground">{selectedRequest.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedRequest.event_date && (
                    <div>
                      <h3 className="text-sm font-medium mb-1 flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" />
                        Date
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(selectedRequest.event_date), 'PPP', { locale: fr })}
                      </p>
                    </div>
                  )}
                  
                  {selectedRequest.event_location && (
                    <div>
                      <h3 className="text-sm font-medium mb-1 flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        Lieu
                      </h3>
                      <p className="text-sm text-muted-foreground">{selectedRequest.event_location}</p>
                    </div>
                  )}
                </div>
                
                {selectedRequest.message && (
                  <div>
                    <h3 className="text-sm font-medium mb-1 flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Message aux destinataires
                    </h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{selectedRequest.message}</p>
                  </div>
                )}
                
                <div>
                  <h3 className="text-sm font-medium mb-1 flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    Destinataires
                  </h3>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="bg-gray-50 p-3 rounded-md text-center">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="font-semibold">{recipientCounts[selectedRequest.id]?.total || 0}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-md text-center">
                      <p className="text-xs text-blue-700">Envoyés</p>
                      <p className="font-semibold text-blue-700">{recipientCounts[selectedRequest.id]?.sent || 0}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-md text-center">
                      <p className="text-xs text-green-700">Complétés</p>
                      <p className="font-semibold text-green-700">{recipientCounts[selectedRequest.id]?.completed || 0}</p>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progression</span>
                      <span className="font-medium">
                        {recipientCounts[selectedRequest.id]?.completed || 0}/{recipientCounts[selectedRequest.id]?.total || 0} ({getProgressPercentage(selectedRequest.id)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div 
                        className="bg-green-600 h-2.5 rounded-full" 
                        style={{ width: `${getProgressPercentage(selectedRequest.id)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="text-xs text-muted-foreground pt-2">
                  Créée le {format(new Date(selectedRequest.created_at), 'PPP à HH:mm', { locale: fr })}
                </div>
              </div>
              
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="sm:mr-auto"
                  onClick={() => setDetailDialogOpen(false)}
                >
                  Fermer
                </Button>
                
                {(selectedRequest.status === 'pending' || selectedRequest.status === 'active') && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        setConfirmDeleteDialogOpen(true);
                      }}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </Button>
                    
                    {selectedRequest.status === 'pending' && (
                      <Button
                        onClick={handleSendEmails}
                        disabled={processingAction}
                        className="flex items-center gap-1"
                      >
                        <Send className="h-4 w-4" />
                        Envoyer les emails
                      </Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteDialogOpen} onOpenChange={setConfirmDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette demande de feedback collective ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteDialogOpen(false)}
              disabled={processingAction}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRequest}
              disabled={processingAction}
            >
              {processingAction ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectiveFeedbacksList;
